/* eslint-disable sort-keys */
import { graphql } from 'gatsby'
import * as React from 'react'

import { Seo } from '../components'
import { DEFAULT_TITLE, sectionsPresets } from '../constants/constData'
import Layout from '../layouts/Layout'
import {
  ALLOWED_WORDS,
  ContentParser,
  findMatchingItems,
  hasNulls,
  replaceWordInString,
} from '../utils/helpers'
import { SliceRenderer } from '../utils/SliceRenderer'

const PageTemplate = ({ data, pageContext }) => {
  const { components, lang, taxonomy, url } = pageContext

  if (!data) return null
  const {
    wpPage: {
      beforeaftersection,
      biopagehero,
      categorycontentsection,
      categorylistingsection,
      ctasectioncontent,
      experiencesectioncontent,
      faqsectioncontent,
      herocontent,
      mapsectioncontent,
      mediasectioncontent,
      pagesections,
      pricedetailscontent,
      promotedtreatments,
      qualitycontentsection,
      related,
      reviewsectioncontent,
      stepssectioncontent,
      teamsectioncontent,
      treatmentdetailssection,
      treatmentintrosection,
      treatmentlistsection,
      treatmentpricingsection,
      treatmentresultsection,
    },
  } = data

  const pageComponents = pagesections?.pagesections?.nodes || []
  const parsedComponents =
    taxonomy === 'treatment' && related?.relatedpages !== null
      ? components.concat(['RelatedArticles'])
      : components

  if (
    taxonomy === 'homepage' &&
    !parsedComponents.includes('PromotedTreatments') &&
    !pageComponents.some(
      (component) => component.fieldGroupName === 'promotedTreatments',
    )
  ) {
    parsedComponents.splice(2, 0, 'PromotedTreatments')
  }

  const matchingItems = findMatchingItems(
    components,
    pageComponents,
    sectionsPresets,
  )

  const contentMap = {
    Herosection: { fieldGroupName: 'herocontent', ...herocontent },
    Awarenesssection: { fieldGroupName: 'awernesssection' },
    MediaSection: {
      fieldGroupName: 'mediasectioncontent',
      ...mediasectioncontent,
    },
    Introsection: {
      ...treatmentintrosection,
      fieldGroupName: 'treatmentintrosection',
    },
    Stepssection: {
      fieldGroupName: 'stepssectioncontent',
      ...stepssectioncontent,
    },
    Teamsection: {
      fieldGroupName: 'teamsectioncontent',
      ...teamsectioncontent,
    },
    Reviewssection: {
      fieldGroupName: 'reviewsectioncontent',
      ...reviewsectioncontent,
    },
    Trustmarksection: { fieldGroupName: 'trustsection' },
    Uspssection: {
      fieldGroupName: 'experiencesectioncontent',
      ...experiencesectioncontent,
    },
    Ctasection: { fieldGroupName: 'ctasectioncontent', ...ctasectioncontent },
    Biolowhero: { fieldGroupName: 'biopagehero', ...biopagehero },
    Categorylisting: {
      fieldGroupName: 'categorylistingsection',
      ...categorylistingsection,
    },
    Categorycontentblock: {
      fieldGroupName: 'categorycontentsection',
      ...categorycontentsection,
    },
    Faqsection: { fieldGroupName: 'faqsectioncontent', ...faqsectioncontent },
    Mapsection: { fieldGroupName: 'mapsectioncontent', ...mapsectioncontent },
    Contentsection: {
      fieldGroupName: 'qualitycontentsection',
      ...qualitycontentsection,
    },
    Pricingdetail: {
      fieldGroupName: 'pricedetailscontent',
      ...pricedetailscontent,
    },
    Treatmentslist: {
      fieldGroupName: 'treatmentlistsection',
      ...treatmentlistsection,
    },
    Treatmentdetail: {
      fieldGroupName: 'treatmentdetailssection',
      ...treatmentdetailssection,
    },
    BeforeSurgerySection: {
      fieldGroupName: 'beforeaftersection',
      ...beforeaftersection,
    },
    Treatmentresult: {
      fieldGroupName: 'treatmentresultsection',
      ...treatmentresultsection,
    },
    Pricingsection: {
      fieldGroupName: 'treatmentpricingsection',
      ...treatmentpricingsection,
    },
    PromotedTreatments: {
      fieldGroupName: 'promotedTreatments',
      ...promotedtreatments,
    },
    ReviewSliderWidget: { fieldGroupName: 'reviewSliderWidget' },
    RelatedArticles: { fieldGroupName: 'bloglistingsection', ...related },
  }

  // GENERATE SLICES ARRAY WITH DEFAULT DATA AND OVERRIDEN DATA
  let slices = parsedComponents.map((component) => {
    const matchingItem = matchingItems.find(
      (item) => item.nodeType === component,
    )
    const defaultData = contentMap[component]

    if (matchingItem && matchingItem[sectionsPresets[component]].hasowndata) {
      const presetData = matchingItem[sectionsPresets[component]]
      return { ...presetData, ...defaultData }
    }
    return { ...defaultData }
  })

  const blogListing = { fieldGroupName: 'bloglistingsection' }
  if (taxonomy === 'blogpage') {
    slices.splice(2, 0, blogListing)
  }

  if (taxonomy === 'treatment') {
    slices = slices.filter(
      (slice) => slice.fieldGroupName !== 'ctasectioncontent',
    )
  }

  if (url === '/mothersday/' || url === '/en/mothersday/') {
    const mothersdayUsp = {
      fieldGroupName: 'mothersdayuspsection',
    }
    const googleReviews = {
      fieldGroupName: 'googleReviews',
    }
    const mothersCta = {
      fieldGroupName: 'mothersdaycta',
    }
    const mothersGiftInfo = {
      fieldGroupName: 'mothersgiftinfo',
    }
    slices.splice(2, 0, mothersGiftInfo)
    slices.splice(3, 0, googleReviews)
    slices.splice(4, 0, mothersdayUsp)
    slices.splice(5, 0, mothersCta)
  }
  return (
    <Layout pageContext={pageContext}>
      <SliceRenderer
        isSurgery={
          url.includes('plastische-chirurgie') ||
          url.includes('plastic-surgery')
        }
        lang={lang.slice(0, 2)}
        pageType={taxonomy}
        slices={slices}
        url={url}
      />
    </Layout>
  )
}

export default PageTemplate

export const Head = ({ data, location, pageContext }) => {
  const { lang, taxonomy } = pageContext
  const {
    wpPage: { herocontent, seo, seoData, title },
  } = data

  const image = herocontent?.herocontentimage?.node
  let treatment
  if (taxonomy === 'treatment') {
    const {
      wpPage: { treatmentfeatureddata, treatmentpricingsection },
    } = data
    treatment = {
      name: replaceWordInString(
        treatmentfeatureddata.featuredname,
        'botox',
        ALLOWED_WORDS[lang],
      ),
      description: replaceWordInString(
        treatmentfeatureddata.description,
        'botox',
        ALLOWED_WORDS[lang],
      ),
      image: treatmentfeatureddata.image,
      offers: ContentParser(
        treatmentpricingsection,
        'treatmentpricecard',
      ).filter((res) => !hasNulls(Object.values(res))),
    }
  }
  const pageTitle =
    seoData?.pagetitle ||
    replaceWordInString(title, 'botox', ALLOWED_WORDS[lang]) ||
    DEFAULT_TITLE
  const metaTitle =
    seoData?.metatitle ||
    replaceWordInString(seo.title, 'botox', ALLOWED_WORDS[lang]) ||
    pageTitle
  const pageDescription =
    seoData?.metadescription ||
    replaceWordInString(seo.metaDesc, 'botox', ALLOWED_WORDS[lang])
  const ogTitle = seoData?.ogtitle || metaTitle
  const ogDescription = seoData?.ogdescription || pageDescription
  const ogImage = seoData?.ogimage?.node || image
  const keywords = seoData?.keywords || undefined
  return (
    <Seo
      canonical={location.pathname}
      description={pageDescription}
      image={ogImage}
      keywords={keywords}
      lang={lang.slice(0, 2)}
      metaTitle={metaTitle}
      ogDescription={ogDescription}
      ogTitle={ogTitle}
      pageType={taxonomy}
      title={pageTitle}
      treatment={treatment}
    />
  )
}

export const query = graphql`
  query ContentPageTemplate($id: String!, $lang: String!) {
    wpPage(id: { eq: $id }, language: { locale: { eq: $lang } }) {
      id
      language {
        code
        locale
        name
        slug
      }
      slug
      title
      pagesections {
        pagesections {
          nodes {
            ... on WpUspssection {
              nodeType
              id
              title
            }
            ... on WpCtasection {
              nodeType
              id
              title
            }
            ... on WpTrustmarksection {
              nodeType
              id
              trustmarkpresets {
                logotypes {
                  nodes {
                    ... on WpTrustitem {
                      nodeType
                      id
                      trustmarkpresets {
                        logo {
                          node {
                            altText
                            sourceUrl
                            localFile {
                              childImageSharp {
                                gatsbyImageData(
                                  quality: 80
                                  webpOptions: { quality: 80 }
                                  avifOptions: { lossless: true, quality: 80 }
                                )
                              }
                            }
                            mediaItemUrl
                            mediaType
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpReviewssection {
              nodeType
              id
              title
              reviewsectionpresets {
                reviewitems {
                  nodes {
                    ... on WpRatingitem {
                      nodeType
                      id
                      ratingitemFields {
                        title
                        scale
                        rating
                        link
                        ratinglogo {
                          node {
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpTeamsection {
              nodeType
              id
              title
              teamsectionpresets {
                hasowndata
                teamcards {
                  nodes {
                    ... on WpTeamcard {
                      nodeType
                      id
                      teamcardFields {
                        cta_en
                        cta_nl
                        title_Nl
                        title_En
                        name
                        image {
                          node {
                            altText
                            sourceUrl
                            localFile {
                              childImageSharp {
                                gatsbyImageData(
                                  quality: 80
                                  webpOptions: { quality: 80 }
                                  avifOptions: { lossless: true, quality: 80 }
                                )
                              }
                            }
                            mediaItemUrl
                            mediaType
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpTreatmentssection {
              nodeType
              id
              title
            }
            ... on WpAwarenesssection {
              nodeType
              id
              title
              ratingssectionpresets {
                title
                kicker
                heading
                hasowndata
                ratingitems {
                  nodes {
                    ... on WpRatingitem {
                      nodeType
                      id
                      ratingitemFields {
                        title
                        scale
                        rating
                        link
                        ratinglogo {
                          node {
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            ... on WpHerosection {
              nodeType
              id
              title
              herosectionpresets {
                hasowndata
                herotype
                herodatafields {
                  text
                  separatorusp2
                  separatorusp1
                  mobilevideoid
                  kicker
                  herosecondhalfcontent
                  herohalfcontent
                  heading
                  discovermorelabel
                  desktopvideoid
                  cta1 {
                    nodes {
                      ... on WpButtoncomponent {
                        nodeType
                        id
                        buttonfields {
                          buttontype
                          linktype
                          url
                          labels {
                            enLabel
                            nl_Label
                          }
                        }
                      }
                    }
                  }
                  cta2 {
                    nodes {
                      ... on WpButtoncomponent {
                        nodeType
                        id
                        buttonfields {
                          buttontype
                          labels {
                            enLabel
                            nl_Label
                          }
                          linktype
                          url
                        }
                      }
                    }
                  }
                  herohalfimage {
                    node {
                      altText
                    }
                  }
                  herosecondhalfimage {
                    node {
                      altText
                    }
                  }
                  herosecondhalfimagemobile {
                    node {
                      altText
                    }
                  }
                  herohalfimagemobile {
                    node {
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
      promotedtreatments {
        treatments {
          nodes {
            ... on WpPage {
              id
              promoteddata {
                description
                title
              }
              uri
              herocontent {
                herocontentimage {
                  node {
                    altText
                    sourceUrl
                    localFile {
                      childImageSharp {
                        gatsbyImageData(
                          quality: 80
                          webpOptions: { quality: 80 }
                          avifOptions: { lossless: true, quality: 80 }
                        )
                      }
                    }
                    mediaItemUrl
                    mediaType
                  }
                }
              }
            }
          }
        }
      }
      herocontent {
        herocontainerwidth
        herocontentheading
        heroseparatorusp2
        heroseparatorusp
        herodiscovermorelabel
        herocta1
        herocta2
        herocontenttext
        herocontentkicker
        herocontentimage {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
        herocontentmobileimage {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      related {
        relatedpages {
          nodes {
            id
            slug
            uri
            ... on WpPage {
              id
              title
              pageType {
                uri
                taxonomy
              }
              seoData {
                metadescription
                pagetitle
              }
              seo {
                title
                metaDesc
              }
              date
              herocontent {
                herocontentimage {
                  node {
                    id
                    altText
                    mediaType
                    mediaItemUrl
                    localFile {
                      childImageSharp {
                        gatsbyImageData(
                          quality: 80
                          webpOptions: { quality: 80 }
                          avifOptions: { lossless: true, quality: 80 }
                        )
                      }
                    }
                  }
                }
              }
            }
            ... on WpPost {
              id
              title
              seoData {
                metadescription
                pagetitle
              }
              seo {
                title
                metaDesc
              }
              date
              herocontent {
                herocontentimage {
                  node {
                    id
                    altText
                    mediaType
                    mediaItemUrl
                    localFile {
                      childImageSharp {
                        gatsbyImageData(
                          quality: 80
                          webpOptions: { quality: 80 }
                          avifOptions: { lossless: true, quality: 80 }
                        )
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      treatmentdetailssection {
        treatmentcontentcard1 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard2 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard3 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard4 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard5 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard6 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard7 {
          treatmentcardcontent
          treatmentcardtitle
        }
        treatmentcontentcard8 {
          treatmentcardtitle
          treatmentcardcontent
        }
        treatmentcontentcard9 {
          treatmentcardtitle
          treatmentcardcontent
        }
        treatmentcontentcard10 {
          treatmentcardtitle
          treatmentcardcontent
        }
        treatmentdetailsbackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
        treatmentdetailscontent
        treatmentdetailsheading
        treatmentdetailskicker
        treatmentdetailstitle
        treatmentdetailswidth
        treatmentsbtnlabel
        membershipbutton {
          link
          title
        }
        treatmentdetailscard1 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard2 {
          cardlink
          cardcontent
          cardkicker
          cardheading
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard3 {
          cardlink
          cardcontent
          cardkicker
          cardheading
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard4 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard5 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard6 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard7 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentdetailscard8 {
          cardlink
          cardcontent
          cardheading
          cardkicker
          cardimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
      }
      quotecontent {
        quotecontainerwidth
        quoteheading
        quotekicker
        quotecontent
        quotebackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      treatmentlistsection {
        treatmentlistcontainerwidth
        treatmentlistcontent
        treatmentlistheading
        treatmentlistkicker
      }
      stepssectioncontent {
        stepskicker
        stepsheading
        stepscontent
        stepsdata {
          step1 {
            stepcontent
            steptitle
          }
          step2 {
            stepcontent
            steptitle
          }
          step3 {
            steptitle
            stepcontent
          }
          step4 {
            stepcontent
            steptitle
          }
          step5 {
            stepcontent
            steptitle
          }
          step6 {
            stepcontent
            steptitle
          }
          step7 {
            stepcontent
            steptitle
          }
          step8 {
            stepcontent
            steptitle
          }
        }
      }
      treatmentsintrocontent {
        treatmentsdescription
        treatmentsheading
        treatmentskicker
        treatmentsgroup1 {
          treatmentscontent
          treatmentstitle
          treatmentscta1
          treatmentscta2
          treatmentspages {
            nodes {
              ... on WpPage {
                id
                title
                uri
                slug
                pageType {
                  uri
                  name
                }
              }
            }
          }
          treatmentsimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentsgroup2 {
          treatmentscontent
          treatmentscta1
          treatmentspages {
            nodes {
              ... on WpPage {
                id
                title
                uri
                slug
                pageType {
                  uri
                  name
                }
              }
            }
          }
          treatmentsimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          treatmentscta2
          treatmentstitle
        }
        treatmentsgroup3 {
          treatmentscontent
          treatmentscta1
          treatmentspages {
            nodes {
              ... on WpPage {
                id
                title
                uri
                slug
                pageType {
                  uri
                  name
                }
              }
            }
          }
          treatmentsimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          treatmentscta2
          treatmentstitle
        }
        treatmentsgroup4 {
          treatmentscontent
          treatmentscta1
          treatmentspages {
            nodes {
              ... on WpPage {
                id
                title
                uri
                slug
                pageType {
                  uri
                  name
                }
              }
            }
          }
          treatmentsimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          treatmentscta2
          treatmentstitle
        }
        treatmentsimage {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
        treatmentscontainerwidth
      }
      teamsectioncontent {
        teamcarousel
        teamcontainerwidth
        teamcontent
        teamheading
        teamkicker
        teamsectioncta
      }
      reviewsectioncontent {
        reviewskicker
        reviewsheading
        reviewscontent
        reviewscontainerwidth
      }
      experiencesectioncontent {
        experiencecontainerwidth
        experiencecontent
        experiencekicker
        experiencetitle
        experienceusp1 {
          usptitle
          uspheading
          uspcontent
          uspimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        experienceusp2 {
          usptitle
          uspheading
          uspcontent
          uspimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        experienceusp3 {
          usptitle
          uspheading
          uspcontent
          uspimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        experienceusp4 {
          usptitle
          uspheading
          uspcontent
          uspimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        experienceimage {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      ctasectioncontent {
        ctacontainerwidth
        ctaheading
        ctakicker
        ctasectioncta
      }
      mediasectioncontent {
        mediasectionurl
        mediacontentrichtext
        mediacontentheading
        mediacontentcontainerwidth
        contentsectionmedia {
          media1 {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          media2 {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
      }
      treatmentintrosection {
        treatmentintroheading
        treatmentintrocontent
        treatmentintrocontainerwidth
        treatmentintrokicker
        ytvideoid
        treatmentintrobackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      biopagehero {
        bioherocontent
        bioheroheader
        bioherokicker
        bioheroimage {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
        bioherobackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      categorylistingsection {
        categorylinklabel
        categorylistingcontent
        categorylistingheader
        categorylistingbackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
        categorylistingtreatments {
          nodes {
            ... on WpPage {
              id
              pageType {
                uri
              }
              treatmentfeatureddata {
                featuredname
                description
                image {
                  node {
                    altText
                    sourceUrl
                    localFile {
                      childImageSharp {
                        gatsbyImageData(
                          quality: 80
                          webpOptions: { quality: 80 }
                          avifOptions: { lossless: true, quality: 80 }
                        )
                      }
                    }
                    mediaItemUrl
                    mediaType
                  }
                }
              }
            }
          }
        }
      }
      categorycontentsection {
        categorycontentheading
        categorycontenttext
        categorycontentvideolink {
          target
          title
          url
        }
        categorycontentbackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      faqsectioncontent {
        faqsectionkicker
        faqsectionheading
        faqitem01 {
          questioncontent
          questiontitle
        }
        faqitem02 {
          questioncontent
          questiontitle
        }
        faqitem03 {
          questioncontent
          questiontitle
        }
        faqitem04 {
          questioncontent
          questiontitle
        }
        faqitem05 {
          questioncontent
          questiontitle
        }
        faqitem06 {
          questioncontent
          questiontitle
        }
        faqitem07 {
          questioncontent
          questiontitle
        }
        faqitem08 {
          questioncontent
          questiontitle
        }
        faqitem09 {
          questioncontent
          questiontitle
        }
        faqitem10 {
          questioncontent
          questiontitle
        }
        faqitem11 {
          questioncontent
          questiontitle
        }
      }
      mapsectioncontent {
        mapcontainerwidth
        mapcontent
        mapkicker
        mapheading
      }
      qualitycontentsection {
        qualityitem01 {
          qualitycontent
          qualityheading
        }
        qualityitem02 {
          qualitycontent
          qualityheading
        }
        qualityitem04 {
          qualitycontent
          qualityheading
        }
        qualityitem03 {
          qualitycontent
          qualityheading
        }
        qualityitem05 {
          qualitycontent
          qualityheading
        }
        qualityitem06 {
          qualitycontent
        }
      }
      pricedetailscontent {
        pricedetailsbuttonlabel
        pricedetailscontainerwidth
        pricedetailscontent
        pricedetailsheading
        pricedetailskicker
      }
      treatmentresultsection {
        doubledpictures
        treatmentresultkicker
        treatmentresulttitle
        textarea
        treatmentresult1 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult2 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult3 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult4 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult5 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult6 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult7 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult8 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult9 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
        treatmentresult10 {
          aftertreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
          beforetreatmentimage {
            node {
              altText
              sourceUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 80
                    webpOptions: { quality: 80 }
                    avifOptions: { lossless: true, quality: 80 }
                  )
                }
              }
              mediaItemUrl
              mediaType
            }
          }
        }
      }
      treatmentpricingsection {
        treatmentfootercontent
        treatmentfootertitle
        treatmentpricingwidth
        treatmentpricingkicker
        treatmentpricingheading
        treatmentpricingcontent
        financebutton {
          link
          title
        }
        membershipbutton {
          link
          title
        }
        treatmentpricecard1 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard2 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard3 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard4 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard5 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard6 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard7 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricecard8 {
          pricecardtitle
          pricecardprice
          pricecardkicker
        }
        treatmentpricingbackground {
          node {
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  webpOptions: { quality: 80 }
                  avifOptions: { lossless: true, quality: 80 }
                )
              }
            }
            mediaItemUrl
            mediaType
          }
        }
      }
      seo {
        title
        metaDesc
        focuskw
        metaKeywords
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
        }
        twitterTitle
        twitterDescription
        twitterImage {
          altText
          sourceUrl
          srcSet
        }
        canonical
        cornerstone
        schema {
          articleType
          pageType
          raw
        }
      }
      seoData {
        keywords
        metadescription
        metatitle
        ogdescription
        ogtitle
        pagetitle
        ogimage {
          node {
            altText
            sourceUrl
            srcSet
          }
        }
      }
      treatmentfeatureddata {
        description
        featuredname
        image {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
    }
  }
`
